<template>
  <div class="ja-funnel-chart">
    <div class="ja-funnel-chart__header">
      <h3 class="ja-funnel-chart__title">Customer Funnel</h3>
      <p class="ja-funnel-chart__subtitle">
        Conversion journey for links created in range
      </p>
    </div>

    <div class="ja-funnel-chart__body">
      <div
        v-for="(stage, index) in stages"
        :key="stage.name"
        class="ja-funnel-chart__stage"
      >
        <div
          class="ja-funnel-chart__stage-bg"
          :style="stageBgStyle(index, stage.color)"
        />
        <div
          class="ja-funnel-chart__stage-content"
          :style="contentStyle(index)"
        >
          <span class="ja-funnel-chart__stage-label">{{ stage.name }}</span>
          <span class="ja-funnel-chart__stage-value">
            {{ formatValue(stage.value) }}
            <span class="ja-funnel-chart__stage-percent">{{ stage.percent }}%</span>
          </span>
        </div>
      </div>
    </div>

    <div class="ja-funnel-chart__revenue">
      <span class="ja-funnel-chart__revenue-icon" aria-hidden="true">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M8 10.5C8 8.567 9.567 7 11.5 7H12.5C14.433 7 16 8.567 16 10.5V11H17.5C18.328 11 19 11.672 19 12.5V16.5C19 17.328 18.328 18 17.5 18H6.5C5.672 18 5 17.328 5 16.5V12.5C5 11.672 5.672 11 6.5 11H8V10.5Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
          <path d="M12 7V5.5C12 4.672 11.328 4 10.5 4H9.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          <circle cx="14.5" cy="14" r="0.75" fill="currentColor"/>
          <path d="M9 14H10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </span>
      <span class="ja-funnel-chart__revenue-text">
        Revenue collected
        <strong>{{ revenue }}</strong>
      </span>
    </div>
  </div>
</template>

<script>
const INSET_STEP = 6.5;

export default {
  name: "CustomerFunnel",
  props: {
    stages: { type: Array, required: true },
    revenue: { type: String, default: "£0" },
  },
  methods: {
    formatValue(value) {
      return Number(value).toLocaleString();
    },
    stageBgStyle(index, color) {
      const topInset = index * INSET_STEP;
      const bottomInset = (index + 1) * INSET_STEP;

      return {
        backgroundColor: color,
        clipPath: `polygon(${topInset}% 0%, ${100 - topInset}% 0%, ${100 - bottomInset}% 100%, ${bottomInset}% 100%)`,
      };
    },
    contentStyle(index) {
      const topInset = index * INSET_STEP;
      const bottomInset = (index + 1) * INSET_STEP;
      const midInset = (topInset + bottomInset) / 2;

      return {
        paddingLeft: `${midInset + 3}%`,
        paddingRight: `${midInset + 3}%`,
      };
    },
  },
};
</script>

<style scoped lang="scss">
.ja-funnel-chart {
  background: #fff;
  border-radius: 18px;
  padding: 1.5rem 1.5rem 1.25rem;
  box-shadow: 0 1px 2px 0 rgba(20, 21, 34, 0.04);
  border: 1px solid #ececf2;
  height: 100%;
  display: flex;
  flex-direction: column;

  &__header {
    margin-bottom: 1.25rem;
  }

  &__title {
    font-size: 1.05rem;
    font-weight: 700;
    color: #2d2d2d;
    margin: 0 0 0.35rem;
    line-height: 1.3;
  }

  &__subtitle {
    font-size: 0.8125rem;
    color: #a8a8b3;
    margin: 0;
    line-height: 1.4;
  }

  &__body {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    flex: 1;
    padding: 0 1rem;
  }

  &__stage {
    position: relative;
    width: 100%;
    max-width: 380px;
    height: 52px;
    flex-shrink: 0;
  }

  &__stage-bg {
    position: absolute;
    inset: 0;
    border-radius: 1px;
  }

  &__stage-content {
    position: relative;
    z-index: 1;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-sizing: border-box;
    color: #fff;
    pointer-events: none;
  }

  &__stage-label {
    font-size: 0.9375rem;
    font-weight: 500;
    letter-spacing: -0.01em;
    white-space: nowrap;
  }

  &__stage-value {
    font-size: 0.9375rem;
    font-weight: 700;
    display: inline-flex;
    align-items: baseline;
    gap: 0.45rem;
    white-space: nowrap;
  }

  &__stage-percent {
    font-weight: 600;
    opacity: 0.95;
  }

  &__revenue {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    align-self: center;
    margin-top: 1.25rem;
    padding: 0.65rem 1.35rem;
    border-radius: 999px;
    background: #ecfdf5;
    border: 1px solid #bbf7d0;
  }

  &__revenue-icon {
    display: inline-flex;
    color: #16a34a;
    flex-shrink: 0;
  }

  &__revenue-text {
    font-size: 0.875rem;
    color: #4b5563;
    line-height: 1.3;

    strong {
      margin-left: 0.35rem;
      font-weight: 700;
      color: #16a34a;
    }
  }
}
</style>
